import React, { useCallback, useEffect, useRef, useState } from "react";
import { PlusIcon, DeleteIcon, CalendarIcon } from "../../../Icons";
import { displayDateUS, formatDateForInput, displayDate } from "../../../utils/dateUtils";
import { searchIcd10Diagnoses } from "../../../services/diagnosisService";
import { searchNpiPhysicians } from "../../../services/physicianService";
import AddDiagnosisModal from "./AddDiagnosisModal";
import AddPhysicianModal from "./AddPhysicianModal";
import { SearchMenuPortal } from "./SearchMenuPortal";
import { PATIENT_THEME } from "../patientTheme";
import YearJumpPopover from "../../../components/datePicker/YearJumpPopover";
import { nativeDatePickerSupportsYearJump } from "../../../components/datePicker/nativeYearJump";

const supportsNativeYearJump = nativeDatePickerSupportsYearJump();

export const rowInputClass = `block w-full rounded-md border border-slate-300 bg-white text-slate-900 text-sm py-1.5 px-2.5 outline-none ${PATIENT_THEME.focus}`;
export const rowLabelClass = "block text-[11px] font-medium text-slate-600 mb-0.5";

const SEARCH_DEBOUNCE_MS = 300;

/** Stable search/spinner indicator — no remount flicker when loading toggles. */
export const SearchFieldStatusIcon = ({ loading }) => (
  <span
    className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4"
    aria-hidden="true"
  >
    <span
      className={`absolute inset-0 m-auto w-3.5 h-3.5 rounded-full border-2 border-indigo-400 border-t-transparent transition-opacity duration-150 ${
        loading ? "opacity-100 animate-spin" : "opacity-0"
      }`}
    />
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={`absolute inset-0 h-4 w-4 text-slate-400 transition-opacity duration-150 ${
        loading ? "opacity-0" : "opacity-100"
      }`}
      viewBox="0 0 20 20"
      fill="currentColor"
    >
      <path
        fillRule="evenodd"
        d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
        clipRule="evenodd"
      />
    </svg>
  </span>
);

const SearchMenuLoading = () => (
  <div className="px-3 py-5 flex items-center justify-center gap-2.5 text-sm text-slate-500">
    <span className="w-4 h-4 rounded-full border-2 border-indigo-400 border-t-transparent animate-spin shrink-0" />
    <span>Searching…</span>
  </div>
);

/** Prefer catalog key; else code before " - " in label; else raw typed text. */
function resolveDiagnosisSearchQuery(value, diagnosisCode) {
  const code = String(diagnosisCode || "").trim();
  if (code) return code;
  const v = String(value || "").trim();
  const m = v.match(/^([A-TV-Z][0-9][A-Z0-9.]{0,6})\s*[-–—]/i);
  if (m) return m[1].toUpperCase();
  return v;
}

function resolvePhysicianSearchQuery(value, npiNumber) {
  const npi = String(npiNumber || "").trim();
  if (npi) return npi;
  return String(value || "").trim();
}

const digitsOnlyNpi = (value) =>
  String(value || "")
    .replace(/\D/g, "")
    .slice(0, 10);

export const RowTextField = ({
  label,
  value,
  onChange,
  placeholder,
  maxLength,
  className = "",
  required = false,
  error = "",
}) => (
  <div className={`space-y-1 ${className}`}>
    <label className={rowLabelClass}>
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      maxLength={maxLength}
      className={`${rowInputClass} ${
        error ? "border-red-400 focus:border-red-500 focus:ring-red-100" : ""
      }`}
      aria-invalid={Boolean(error)}
    />
    {error ? <p className="text-[11px] text-red-600">{error}</p> : null}
  </div>
);

export const RowTextAreaField = ({
  label,
  value,
  onChange,
  placeholder,
  maxLength,
  className = "",
  required = false,
  error = "",
  rows = 3,
}) => (
  <div className={`space-y-1 ${className}`}>
    <label className={rowLabelClass}>
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      maxLength={maxLength}
      rows={rows}
      className={`${rowInputClass} resize-y min-h-[72px] ${
        error ? "border-red-400 focus:border-red-500 focus:ring-red-100" : ""
      }`}
      aria-invalid={Boolean(error)}
    />
    {error ? <p className="text-[11px] text-red-600">{error}</p> : null}
  </div>
);

export const RowSelectField = ({
  label,
  value,
  onChange,
  options,
  placeholder,
  className = "",
  required = false,
  error = "",
}) => (
  <div className={`space-y-1 ${className}`}>
    <label className={rowLabelClass}>
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <select
      value={value}
      onChange={onChange}
      className={`${rowInputClass} ${
        error ? "border-red-400 focus:border-red-500 focus:ring-red-100" : ""
      }`}
      aria-invalid={Boolean(error)}
    >
      <option value="">{placeholder || "Select"}</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
    {error ? <p className="text-[11px] text-red-600">{error}</p> : null}
  </div>
);

/**
 * Allergy dropdown + optional “Other” free-text field beside it.
 * `row`: { allergyName, otherText }
 */
export const AllergyRowFields = ({
  row,
  index = 0,
  onSelectChange,
  onOtherTextChange,
  options = [],
  error = "",
}) => {
  const isOther = String(row?.allergyName || "").trim() === "Other";
  const label = index === 0 ? "Allergy" : `Allergy ${index + 1}`;

  return (
    <>
      <RowSelectField
        label={label}
        value={row?.allergyName || ""}
        onChange={(e) => onSelectChange?.(e.target.value)}
        options={options}
        placeholder="Select common allergy"
        className={
          isOther
            ? "lg:col-span-4 sm:col-span-1"
            : "lg:col-span-6 sm:col-span-2 max-w-xl"
        }
        error={!isOther ? error : ""}
      />
      {isOther ? (
        <RowTextField
          label="Specify allergy"
          value={row?.otherText || ""}
          onChange={(e) => onOtherTextChange?.(e.target.value)}
          placeholder="Type custom allergy…"
          maxLength={255}
          className="lg:col-span-7 sm:col-span-1"
          required
          error={error}
        />
      ) : (
        <div className="hidden lg:block lg:col-span-5" aria-hidden="true" />
      )}
    </>
  );
};

/** Read-only allergy chips for demographics / summary surfaces. */
export const AllergyChips = ({
  allergies = [],
  emptyLabel = "—",
  className = "",
}) => {
  const names = (Array.isArray(allergies) ? allergies : [])
    .map((row) => {
      if (typeof row === "string") return row.trim();
      const other = String(row?.otherText || "").trim();
      const name = String(row?.allergyName || "").trim();
      if (name === "Other") return other;
      return name || other;
    })
    .filter(Boolean);

  if (!names.length) {
    return (
      <p className={`text-sm text-slate-400 font-medium ${className}`}>
        {emptyLabel}
      </p>
    );
  }

  return (
    <div className={`flex flex-wrap gap-1.5 ${className}`}>
      {names.map((name, idx) => (
        <span
          key={`${name}-${idx}`}
          className="inline-flex items-center max-w-full px-2.5 py-1 rounded-md text-xs sm:text-sm font-medium text-indigo-800 bg-indigo-50 border border-indigo-100/80 truncate"
          title={name}
        >
          {name}
        </span>
      ))}
    </div>
  );
};

/** Billable tick — auto-set from ICD catalog; clean checkbox (no tinted box). */
export const RowBillableField = ({
  label = "Billable",
  checked = false,
  onChange,
  className = "",
  disabled = false,
}) => (
  <div className={`space-y-1 ${className}`}>
    <label className={rowLabelClass}>{label}</label>
    <label
      className={`inline-flex items-center gap-2 h-[34px] select-none ${
        disabled ? "cursor-not-allowed" : "cursor-pointer"
      }`}
    >
      <input
        type="checkbox"
        className="h-4 w-4 rounded border-slate-300 text-indigo-500 focus:ring-indigo-200 focus:ring-offset-0 disabled:opacity-100 disabled:cursor-not-allowed"
        checked={Boolean(checked)}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
      />
      <span
        className={`text-xs font-medium whitespace-nowrap ${
          checked ? "text-indigo-700" : "text-slate-500"
        }`}
      >
        {checked ? "Billable" : "Not billable"}
      </span>
    </label>
  </div>
);

/** ICD-10 catalog autocomplete; search only after click/type (not on edit open). */
export const DiagnosisSearchField = ({
  label,
  value,
  diagnosisCode,
  onChangeText,
  onSelectCatalog,
  onClearCatalog,
  placeholder = "Search code or diagnosis…",
  className = "",
  required = false,
}) => {
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [searchEnabled, setSearchEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [isDefaultList, setIsDefaultList] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);
  const defaultCacheRef = useRef(null);
  const skipDebounceOnceRef = useRef(false);

  const closeMenu = useCallback(() => {
    setOpen(false);
    setSearchEnabled(false);
  }, []);

  const fetchSuggestions = useCallback(async (q, { openMenu = true } = {}) => {
    const trimmed = String(q || "").trim();
    // Instant cache hit — no loading flash
    if (!trimmed && defaultCacheRef.current) {
      requestIdRef.current += 1;
      setResults(defaultCacheRef.current);
      setIsDefaultList(true);
      setLoading(false);
      if (openMenu) setOpen(true);
      return;
    }

    const reqId = ++requestIdRef.current;
    setLoading(true);
    try {
      const res = await searchIcd10Diagnoses({
        q: trimmed,
        limit: trimmed ? 12 : 10,
      });
      if (reqId !== requestIdRef.current) return;
      const payload = res.result || res;
      const diagnoses = payload.diagnoses || [];
      if (!trimmed) defaultCacheRef.current = diagnoses;
      setResults(diagnoses);
      setIsDefaultList(!trimmed);
      if (openMenu) setOpen(true);
    } catch {
      if (reqId !== requestIdRef.current) return;
      setResults([]);
      setIsDefaultList(false);
      if (openMenu) setOpen(true);
    } finally {
      if (reqId === requestIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      const t = e.target;
      if (wrapRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      closeMenu();
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [closeMenu]);

  // Debounced search only after user click/type enables it
  useEffect(() => {
    if (!searchEnabled) return undefined;
    if (skipDebounceOnceRef.current) {
      skipDebounceOnceRef.current = false;
      return undefined;
    }
    const q = resolveDiagnosisSearchQuery(value, diagnosisCode);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (q.length === 0) {
      debounceRef.current = setTimeout(() => fetchSuggestions(""), SEARCH_DEBOUNCE_MS);
      return () => clearTimeout(debounceRef.current);
    }
    if (q.length < 2 && !diagnosisCode) {
      setResults([]);
      setIsDefaultList(false);
      setLoading(false);
      setOpen(true);
      return undefined;
    }

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(q);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, diagnosisCode, searchEnabled, fetchSuggestions]);

  const enableSearch = () => {
    skipDebounceOnceRef.current = true;
    setSearchEnabled(true);
    const q = resolveDiagnosisSearchQuery(value, diagnosisCode);
    if (q.length >= 1) fetchSuggestions(q);
    else fetchSuggestions("");
  };

  const activeQuery = resolveDiagnosisSearchQuery(value, diagnosisCode);
  const showEmptyAdd =
    open &&
    searchEnabled &&
    !loading &&
    activeQuery.length >= 2 &&
    !diagnosisCode &&
    results.length === 0;

  return (
    <div className={`space-y-1 ${className}`}>
      <label className={rowLabelClass}>
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div ref={wrapRef} className="relative">
        <input
          ref={inputRef}
          type="text"
          className={`${rowInputClass} pr-9`}
          value={value}
          placeholder={placeholder}
          autoComplete="off"
          onClick={enableSearch}
          onChange={(e) => {
            setSearchEnabled(true);
            const next = e.target.value;
            onChangeText(next);
            if (diagnosisCode) onClearCatalog?.();
          }}
        />
        <SearchFieldStatusIcon loading={loading} />
        <SearchMenuPortal
          anchorRef={inputRef}
          menuRef={menuRef}
          open={open && (loading || results.length > 0 || showEmptyAdd)}
        >
          {loading && results.length === 0 ? (
            <SearchMenuLoading />
          ) : results.length > 0 ? (
            <>
              <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 bg-slate-50 border-b border-slate-100 sticky top-0">
                {isDefaultList ? "Suggested diagnoses" : "Search results"}
                {loading ? " · Updating…" : ""}
              </div>
              <ul>
                {results.map((item) => (
                  <li key={item.code}>
                    <button
                      type="button"
                      className={`w-full text-left px-3.5 py-2.5 border-b border-slate-50 last:border-0 transition-colors ${PATIENT_THEME.menuHover}`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        onSelectCatalog(item);
                        closeMenu();
                      }}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-sm font-medium text-slate-900 leading-snug">
                          {item.label || `${item.code} - ${item.description}`}
                        </span>
                        <span className="shrink-0 flex flex-col items-end gap-1">
                          {item.isBillable === false ||
                          item.primaryAllowed === false ? (
                            <span className="text-[10px] font-medium text-slate-500">
                              Not billable
                            </span>
                          ) : (
                            <span className="text-[10px] font-medium text-indigo-600">
                              Billable
                            </span>
                          )}
                          {item.clinicalGroup ? (
                            <span className="text-[10px] font-medium text-slate-500 max-w-[11rem] text-right leading-tight">
                              {item.clinicalGroup}
                            </span>
                          ) : null}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center justify-between gap-2">
                        <span className="text-[11px] text-slate-500 font-mono">
                          {item.code}
                        </span>
                        {item.requiresCodeFirst ? (
                          <span className="text-[10px] font-medium text-indigo-700">
                            Code First
                          </span>
                        ) : null}
                      </div>
                      {item.billingWarning ? (
                        <div className="mt-1 text-[10px] leading-snug text-amber-700">
                          {item.billingWarning}
                        </div>
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : showEmptyAdd ? (
            <>
              <div className="px-3.5 py-3 text-[12px] text-slate-600 border-b border-slate-100">
                No catalog match found for this search.
              </div>
              <div className="px-3 py-2.5 bg-slate-50">
                <button
                  type="button"
                  className={`inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold rounded-md border ${PATIENT_THEME.button.outline}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    closeMenu();
                    setAddOpen(true);
                  }}
                >
                  <PlusIcon size={13} />
                  Add
                </button>
              </div>
            </>
          ) : null}
        </SearchMenuPortal>
      </div>
      <AddDiagnosisModal
        isOpen={addOpen}
        initialCode={value}
        initialDescription={value}
        onClose={() => setAddOpen(false)}
        onCreated={(diagnosis) => {
          defaultCacheRef.current = null;
          onSelectCatalog(diagnosis);
          setAddOpen(false);
          closeMenu();
        }}
      />
    </div>
  );
};

/**
 * NPI physician catalog autocomplete.
 * mode="name" — search by display name (or typed NPI).
 * mode="npi"  — same dropdown, digits-only input bound to NPI Number.
 * Search starts only after click/type (not on edit open).
 */
export const PhysicianSearchField = ({
  label,
  value,
  npiNumber,
  onChangeText,
  onSelectCatalog,
  onClearCatalog,
  placeholder = "Search physician name or NPI…",
  className = "",
  required = false,
  error = "",
  mode = "name",
}) => {
  const isNpiMode = mode === "npi";
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [searchEnabled, setSearchEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [isDefaultList, setIsDefaultList] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);
  const defaultCacheRef = useRef(null);
  const skipDebounceOnceRef = useRef(false);

  const closeMenu = useCallback(() => {
    setOpen(false);
    setSearchEnabled(false);
  }, []);

  const resolveQuery = useCallback(
    (text, linkedNpi) => {
      if (isNpiMode) return digitsOnlyNpi(text);
      return resolvePhysicianSearchQuery(text, linkedNpi);
    },
    [isNpiMode],
  );

  const fetchSuggestions = useCallback(async (q, { openMenu = true } = {}) => {
    const trimmed = String(q || "").trim();
    if (!trimmed && defaultCacheRef.current) {
      requestIdRef.current += 1;
      setResults(defaultCacheRef.current);
      setIsDefaultList(true);
      setLoading(false);
      if (openMenu) setOpen(true);
      return;
    }

    const reqId = ++requestIdRef.current;
    setLoading(true);
    try {
      const res = await searchNpiPhysicians({
        q: trimmed,
        limit: trimmed ? 12 : 10,
      });
      if (reqId !== requestIdRef.current) return;
      const payload = res.result || res;
      const physicians = payload.physicians || [];
      if (!trimmed) defaultCacheRef.current = physicians;
      setResults(physicians);
      setIsDefaultList(!trimmed);
      if (openMenu) setOpen(true);
    } catch {
      if (reqId !== requestIdRef.current) return;
      setResults([]);
      setIsDefaultList(false);
      if (openMenu) setOpen(true);
    } finally {
      if (reqId === requestIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      const t = e.target;
      if (wrapRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      closeMenu();
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [closeMenu]);

  useEffect(() => {
    if (!searchEnabled) return undefined;
    if (skipDebounceOnceRef.current) {
      skipDebounceOnceRef.current = false;
      return undefined;
    }
    const q = resolveQuery(value, npiNumber);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (q.length === 0) {
      debounceRef.current = setTimeout(() => fetchSuggestions(""), SEARCH_DEBOUNCE_MS);
      return () => clearTimeout(debounceRef.current);
    }
    // Name mode needs 2 chars unless already linked to an NPI; NPI mode searches from 1 digit.
    const minLen = isNpiMode ? 1 : 2;
    if (q.length < minLen && !( !isNpiMode && npiNumber)) {
      setResults([]);
      setIsDefaultList(false);
      setLoading(false);
      setOpen(true);
      return undefined;
    }

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(q);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, npiNumber, searchEnabled, fetchSuggestions, resolveQuery, isNpiMode]);

  const enableSearch = () => {
    skipDebounceOnceRef.current = true;
    setSearchEnabled(true);
    const q = resolveQuery(value, npiNumber);
    if (q.length >= 1) fetchSuggestions(q);
    else fetchSuggestions("");
  };

  const activeQuery = resolveQuery(value, npiNumber);
  const linkedToCatalog = !isNpiMode && Boolean(npiNumber);
  const showEmptyAdd =
    open &&
    searchEnabled &&
    !loading &&
    activeQuery.length >= (isNpiMode ? 4 : 2) &&
    !linkedToCatalog &&
    results.length === 0;

  return (
    <div className={`space-y-1 ${className}`}>
      <label className={rowLabelClass}>
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div ref={wrapRef} className="relative">
        <input
          ref={inputRef}
          type="text"
          inputMode={isNpiMode ? "numeric" : undefined}
          maxLength={isNpiMode ? 10 : undefined}
          className={`${rowInputClass} pr-9 ${
            isNpiMode ? "font-mono tabular-nums" : ""
          } ${
            error ? "border-red-400 focus:border-red-500 focus:ring-red-100" : ""
          }`}
          value={value}
          placeholder={placeholder}
          autoComplete="off"
          aria-invalid={Boolean(error)}
          onClick={enableSearch}
          onChange={(e) => {
            setSearchEnabled(true);
            const next = isNpiMode
              ? digitsOnlyNpi(e.target.value)
              : e.target.value;
            onChangeText(next);
            // Name edits after a catalog pick clear the linked NPI.
            // NPI mode owns the NPI value — never wipe it while typing.
            if (!isNpiMode && npiNumber) onClearCatalog?.();
          }}
        />
        <SearchFieldStatusIcon loading={loading} />
        <SearchMenuPortal
          anchorRef={inputRef}
          menuRef={menuRef}
          open={open && (loading || results.length > 0 || showEmptyAdd)}
        >
          {loading && results.length === 0 ? (
            <SearchMenuLoading />
          ) : results.length > 0 ? (
            <>
              <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 bg-slate-50 border-b border-slate-100 sticky top-0">
                {isDefaultList ? "Suggested physicians" : "Search results"}
                {loading ? " · Updating…" : ""}
              </div>
              <ul>
                {results.map((item) => (
                  <li key={item.npi}>
                    <button
                      type="button"
                      className={`w-full text-left px-3.5 py-2.5 border-b border-slate-50 last:border-0 transition-colors ${PATIENT_THEME.menuHover}`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        onSelectCatalog(item);
                        closeMenu();
                      }}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-sm font-medium text-slate-900 leading-snug">
                          {item.displayName}
                        </span>
                        {item.specialty || item.taxonomyDescription ? (
                          <span className={`shrink-0 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${PATIENT_THEME.chip}`}>
                            {item.specialty || item.taxonomyDescription}
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
                        <span className="font-mono text-indigo-700">NPI: {item.npi}</span>
                        {item.credential ? <span>{item.credential}</span> : null}
                        {item.city || item.state ? (
                          <span>
                            {[item.city, item.state].filter(Boolean).join(", ")}
                          </span>
                        ) : null}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : showEmptyAdd ? (
            <>
              <div className="px-3.5 py-3 text-[12px] text-slate-600 border-b border-slate-100">
                No catalog match found for this search.
              </div>
              <div className="px-3 py-2.5 bg-slate-50">
                <button
                  type="button"
                  className={`inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold rounded-md border ${PATIENT_THEME.button.outline}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    closeMenu();
                    setAddOpen(true);
                  }}
                >
                  <PlusIcon size={13} />
                  Add Physician
                </button>
              </div>
            </>
          ) : null}
        </SearchMenuPortal>
      </div>
      {error ? <p className="text-[11px] text-red-600">{error}</p> : null}
      <AddPhysicianModal
        isOpen={addOpen}
        initialName={isNpiMode ? "" : value}
        initialNpi={isNpiMode ? value : npiNumber || ""}
        onClose={() => setAddOpen(false)}
        onCreated={(physician) => {
          defaultCacheRef.current = null;
          onSelectCatalog(physician);
          setAddOpen(false);
          closeMenu();
        }}
      />
    </div>
  );
};

/** Controlled mm/dd/yyyy text field. Safari opens a year-jump calendar. */
export const RowDateField = ({ label, value, onChange, className = "", required = false }) => {
  const dateRef = useRef(null);
  const anchorRef = useRef(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const ymd = formatDateForInput(value) || "";
  const display = value
    ? displayDateUS(value) || (String(value).match(/^\d{2}\/\d{2}\/\d{4}$/) ? value : "")
    : "";
  const [localText, setLocalText] = useState(display);

  useEffect(() => {
    setLocalText(display);
  }, [display]);

  const emitFromText = (text) => {
    const m = String(text).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (m) {
      onChange(`${m[1]}/${m[2]}/${m[3]}`);
      return;
    }
    if (!text) onChange("");
  };

  const openPicker = () => {
    if (!supportsNativeYearJump) {
      setPickerOpen(true);
      return;
    }
    if (dateRef.current?.showPicker) {
      try {
        dateRef.current.showPicker();
      } catch {
        dateRef.current.focus();
      }
    } else {
      dateRef.current?.focus();
    }
  };

  return (
    <div className={`space-y-1 ${className}`}>
      <label className={rowLabelClass}>
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative" ref={anchorRef}>
        <input
          type="text"
          className={`${rowInputClass} pr-8`}
          value={localText}
          placeholder="mm/dd/yyyy"
          maxLength={10}
          onChange={(e) => {
            let val = e.target.value.replace(/[^0-9/]/g, "");
            if (val.length === 2 && localText.length === 1 && !val.includes("/")) {
              val += "/";
            } else if (
              val.length === 5 &&
              localText.length === 4 &&
              val.split("/").length === 2
            ) {
              val += "/";
            }
            setLocalText(val);
            emitFromText(val);
          }}
          onBlur={() => {
            if (localText) setLocalText(localText.replace(/-/g, "/"));
          }}
        />
        <input
          type="date"
          ref={dateRef}
          value={ymd}
          tabIndex={-1}
          className="absolute w-0 h-0 opacity-0 pointer-events-none"
          onChange={(e) => {
            const next = e.target.value;
            if (!next) {
              setLocalText("");
              onChange("");
              return;
            }
            const shown = displayDateUS(next) || displayDate(next)?.replace(/-/g, "/") || "";
            setLocalText(shown);
            onChange(shown);
          }}
        />
        <button
          type="button"
          onClick={openPicker}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          title="Open calendar"
          aria-label={`Pick ${label}`}
        >
          <CalendarIcon size={14} />
        </button>
        {!supportsNativeYearJump && (
          <YearJumpPopover
            open={pickerOpen}
            anchorRef={anchorRef}
            value={ymd}
            onClose={() => setPickerOpen(false)}
            onSelect={(next) => {
              const shown = displayDateUS(next) || "";
              setLocalText(shown);
              onChange(shown);
              setPickerOpen(false);
            }}
          />
        )}
      </div>
    </div>
  );
};

export const AddRowButton = ({
  label,
  onClick,
  tone = PATIENT_THEME.section.addTone,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium border bg-white hover:bg-opacity-60 rounded-md ${tone}`}
  >
    <PlusIcon size={13} />
    {label}
  </button>
);

export const RemoveRowButton = ({ onClick, label = "Remove", className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center justify-center gap-1.5 h-9 w-full lg:w-9 px-2.5 text-xs font-medium text-red-700 border border-red-200 bg-white hover:bg-red-50 rounded-md ${className}`}
    title={label}
    aria-label={label}
  >
    <DeleteIcon size={13} />
    <span className="lg:hidden">Remove</span>
  </button>
);
